/**
 * Item Management - Item Master Data List Page
 * List + FilterPanel + Export + Detail Drawer (5 Tabs)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Button, Table, Space, Col, message, Upload, Modal, Image } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { ExportOutlined, ImportOutlined, InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../../../components/common/FilterPanel';
import type { ItemDTO, QueryItemPageRequest } from '../../../../types/master-data/item';
import type { Paging } from '../../../../types/common';
import * as itemService from '../../../../services/master-data/itemService';
import {
  ItemStatusOptions,
} from '../../../../constants/masterData';
import ItemDetail from '../components/ItemDetail';

const ItemList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<ItemDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // Drawer state
  const [detailVisible, setDetailVisible] = useState(false);
  const [viewingCode, setViewingCode] = useState<string>('');
  const [viewingOwnerCode, setViewingOwnerCode] = useState<string>('');

  // Import modal state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls,.csv',
    fileList,
    beforeUpload: (file) => {
      setFileList([file as unknown as UploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a file first');
      return;
    }

    const file = fileList[0] as unknown as File;
    setImportLoading(true);

    try {
      message.loading('Uploading file...', 0);
      const response = await itemService.importItemFile(file);

      if ((response as any).data?.success !== false) {
        message.destroy();
        message.success('Item file import submitted successfully');
        setImportModalVisible(false);
        setFileList([]);
        loadData(buildQueryParams(1, pagination.pageSize));

        // If backend returns a result file (e.g. error report), auto-download
        const resultData = (response as any).data?.data;
        if (resultData?.downloadUrl) {
          const link = document.createElement('a');
          link.href = resultData.downloadUrl;
          link.download = resultData.fileName || 'import_result.xlsx';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        message.destroy();
        message.error((response as any).data?.errorMessage || 'Import failed');
      }
    } catch (err: any) {
      message.destroy();
      console.error('Import error:', err);
      message.error(err.message || 'Import failed, please try again');
    } finally {
      setImportLoading(false);
    }
  };

  // Load list data
  const loadData = useCallback(async (params: QueryItemPageRequest) => {
    setLoading(true);
    try {
      const response = await itemService.pageItem(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // Build query params
  const buildQueryParams = (page: number, pageSize: number): QueryItemPageRequest => {
    const values = searchForm.getFieldsValue();
    return {
      currentPage: page,
      pageSize,
      code: values.code || undefined,
      name: values.name || undefined,
      ownerCode: values.ownerCode || undefined,
      category: values.category || undefined,
      brandCode: values.brandCode || undefined,
      status: values.status || undefined,
    };
  };

  // Search
  const handleSearch = () => {
    loadData(buildQueryParams(1, pagination.pageSize));
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Page change
  const handlePageChange = (page: number, pageSize: number) => {
    loadData(buildQueryParams(page, pageSize));
  };

  // Export
  const [exportLoading, setExportLoading] = useState(false);
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const response = await itemService.exportItem({
        status: values.status || undefined,
        code: values.code || undefined,
        name: values.name || undefined,
        ownerCode: values.ownerCode || undefined,
        brandCode: values.brandCode || undefined,
      });
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('Export successful');
      } else {
        message.error(response.errorMessage || 'Export failed');
      }
    } catch {
      message.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // View detail
  const handleViewDetail = (record: ItemDTO) => {
    setViewingCode(record.code);
    setViewingOwnerCode(record.ownerCode);
    setDetailVisible(true);
  };

  // Table columns - aligned with expected field list
  const columns = [
    {
      title: 'SKU',
      dataIndex: 'code',
      key: 'code',
      width: 140,
      fixed: 'left' as const,
    },
    {
      title: 'NAME_TH',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Name_EN',
      dataIndex: 'englishName',
      key: 'englishName',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Sell_Unit',
      dataIndex: 'sellUnit',
      key: 'sellUnit',
      width: 100,
    },
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 160,
    },
    {
      title: 'Price_GF',
      dataIndex: 'priceGf',
      key: 'priceGf',
      width: 100,
      render: (val: number | null) => val != null ? val.toFixed(2) : '-',
    },
    {
      title: 'Price_HY',
      dataIndex: 'priceHy',
      key: 'priceHy',
      width: 100,
      render: (val: number | null) => val != null ? val.toFixed(2) : '-',
    },
    {
      title: 'Item_Sold_By',
      dataIndex: 'itemSoldBy',
      key: 'itemSoldBy',
      width: 120,
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 100,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 100,
    },
    {
      title: 'Product_Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (url: string | null, record: ItemDTO) => {
        if (!url) return '-';
        const previewSrc = record.thumbnailImage || url;
        return (
          <Image
            src={previewSrc}
            preview={{ src: url }}
            width={48}
            height={48}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg=="
          />
        );
      },
    },
    {
      title: 'Thumbnail_Image',
      dataIndex: 'thumbnailImage',
      key: 'thumbnailImage',
      width: 100,
      render: (url: string | null) => url ? (
        <Image
          src={url}
          width={48}
          height={48}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg=="
        />
      ) : '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },
    {
      title: 'STATUS_GF',
      dataIndex: 'productStatusGf',
      key: 'productStatusGf',
      width: 120,
    },
    {
      title: 'STATUS_HY',
      dataIndex: 'productStatusHy',
      key: 'productStatusHy',
      width: 120,
    },
    {
      title: 'Brand_Id',
      dataIndex: 'brandCode',
      key: 'brandCode',
      width: 120,
    },
    {
      title: 'Brand_Name_En',
      dataIndex: 'brandNameEn',
      key: 'brandNameEn',
      width: 140,
    },
    {
      title: 'Brand_Name_Th',
      dataIndex: 'brandNameTh',
      key: 'brandNameTh',
      width: 140,
    },
    {
      title: 'Dimension_Length',
      dataIndex: 'length',
      key: 'length',
      width: 140,
      render: (val: number | null) => val != null ? String(val) : '-',
    },
    {
      title: 'Dimension_Height',
      dataIndex: 'height',
      key: 'height',
      width: 140,
      render: (val: number | null) => val != null ? String(val) : '-',
    },
    {
      title: 'Dimension_Width',
      dataIndex: 'width',
      key: 'width',
      width: 140,
      render: (val: number | null) => val != null ? String(val) : '-',
    },
    {
      title: 'Weight',
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (val: number | null) => val != null ? String(val) : '-',
    },
    {
      title: 'CAT_GF_LV1_ID',
      dataIndex: 'catGfLv1Id',
      key: 'catGfLv1Id',
      width: 140,
    },
    {
      title: 'CAT_GF_LV1_NAME',
      dataIndex: 'catGfLv1Name',
      key: 'catGfLv1Name',
      width: 160,
    },
    {
      title: 'CAT_GF_LV2_ID',
      dataIndex: 'catGfLv2Id',
      key: 'catGfLv2Id',
      width: 140,
    },
    {
      title: 'CAT_GF_LV2_NAME',
      dataIndex: 'catGfLv2Name',
      key: 'catGfLv2Name',
      width: 160,
    },
    {
      title: 'CAT_GF_LV3_ID',
      dataIndex: 'catGfLv3Id',
      key: 'catGfLv3Id',
      width: 140,
    },
    {
      title: 'CAT_GF_LV3_NAME',
      dataIndex: 'catGfLv3Name',
      key: 'catGfLv3Name',
      width: 160,
    },
    {
      title: 'CAT_HY_LV1_ID',
      dataIndex: 'catHyLv1Id',
      key: 'catHyLv1Id',
      width: 140,
    },
    {
      title: 'CAT_HY_LV1_NAME',
      dataIndex: 'catHyLv1Name',
      key: 'catHyLv1Name',
      width: 160,
    },
    {
      title: 'CAT_HY_LV2_ID',
      dataIndex: 'catHyLv2Id',
      key: 'catHyLv2Id',
      width: 140,
    },
    {
      title: 'CAT_HY_LV2_NAME',
      dataIndex: 'catHyLv2Name',
      key: 'catHyLv2Name',
      width: 160,
    },
    {
      title: 'CAT_HY_LV3_ID',
      dataIndex: 'catHyLv3Id',
      key: 'catHyLv3Id',
      width: 140,
    },
    {
      title: 'CAT_HY_LV3_NAME',
      dataIndex: 'catHyLv3Name',
      key: 'catHyLv3Name',
      width: 160,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: ItemDTO) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            Detail
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Filter Area */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="code" label="Item Code">
            <Input placeholder="Enter item code" allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="name" label="Item Name">
            <Input placeholder="Enter item name" allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="ownerCode" label="Owner Code">
            <Input placeholder="Enter owner code" allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status" label="Status">
            <Select
              placeholder="Select"
              allowClear
              options={ItemStatusOptions.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* Action Bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
        <Button icon={<ExportOutlined />} loading={exportLoading} onClick={handleExport}>
          Export
        </Button>
      </div>

      {/* Table */}
      <Table
        rowKey="code"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        scroll={{ x: 5000, y: 'calc(100vh - 380px)' }}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.totalCount,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
          onChange: handlePageChange,
        }}
      />

      {/* Detail Drawer */}
      {detailVisible && viewingCode && (
        <ItemDetail
          visible={detailVisible}
          code={viewingCode}
          ownerCode={viewingOwnerCode}
          onClose={() => setDetailVisible(false)}
          onSave={() => {
            setDetailVisible(false);
            loadData(buildQueryParams(pagination.currentPage, pagination.pageSize));
          }}
        />
      )}

      {/* Import Modal */}
      <Modal
        title="Import Item Data"
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>Upload a CSV or Excel file to import item data via SFTP simulation.</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            Supported formats: .xlsx, .xls, .csv
          </p>
        </div>

        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">Only single file upload is supported.</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default ItemList;
